import * as Sentry from "@sentry/react";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useActiveTheme } from "@/hooks/useActiveTheme";
import { usePresenceTracker } from "@/hooks/usePresence";
import { useFeedbackRequest } from "@/hooks/useFeedbackRequest";
import { usePageVisitTracker } from "@/hooks/usePageVisitTracker";
import { PlatformLogoProvider } from "@/hooks/usePlatformLogo";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";
import { AdminImpersonationProvider } from "@/contexts/AdminImpersonationContext";
import MaintenanceMode from "@/components/MaintenanceMode";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackRequestDialog } from "@/components/FeedbackRequestDialog";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/lib/chunkErrorRecovery";

// Eager load the home page for fast first paint
import StartClinic from "./pages/clinic/StartClinic";

// Lazy load all other pages
const Programs = lazy(() => import("./pages/Programs"));
const Index = lazy(() => import("./pages/Index"));
const CalorieCalculator = lazy(() => import("./pages/CalorieCalculator"));
const DietitianCalculator = lazy(() => import("./pages/DietitianCalculator"));
const RenalCalculator = lazy(() => import("./pages/RenalCalculator"));
const FormulaCalculator = lazy(() => import("./pages/FormulaCalculator"));
const FoodDatabase = lazy(() => import("./pages/FoodDatabase"));
const MealPlanner = lazy(() => import("./pages/MealPlanner"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
import ResetPassword from "./pages/ResetPassword";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Analytics = lazy(() => import("./pages/Analytics"));
const ProductManagement = lazy(() => import("./pages/ProductManagement"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const TestimonialsManagement = lazy(() => import("./pages/TestimonialsManagement"));
const ContactMessagesManagement = lazy(() => import("./pages/ContactMessagesManagement"));
const Payment = lazy(() => import("./pages/Payment"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const PurchasesManagement = lazy(() => import("./pages/PurchasesManagement"));
const SpecialistDashboard = lazy(() => import("./pages/SpecialistDashboard"));
const PerformanceReports = lazy(() => import("./pages/PerformanceReports"));
const RenewalPayment = lazy(() => import("./pages/RenewalPayment"));
const UnlockClients = lazy(() => import("./pages/UnlockClients"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const FreeTrial = lazy(() => import("./pages/FreeTrial"));
const About = lazy(() => import("./pages/About"));
const SpecialServicesManagement = lazy(() => import("./pages/SpecialServicesManagement"));
const ClinicRegister = lazy(() => import("./pages/clinic/ClinicRegister"));
const ClinicPending = lazy(() => import("./pages/clinic/ClinicPending"));
const ClinicPayment = lazy(() => import("./pages/clinic/ClinicPayment"));
const ClinicPaymentCallback = lazy(() => import("./pages/clinic/ClinicPaymentCallback"));
const ClinicDashboard = lazy(() => import("./pages/clinic/ClinicDashboard"));
const OrgDashboard = lazy(() => import("./pages/OrgDashboard"));
const ClinicRenew = lazy(() => import("./pages/clinic/ClinicRenew"));
const ClinicMealDesignerAddon = lazy(() => import("./pages/clinic/ClinicMealDesignerAddon"));
import ClinicPublicPage from "./pages/clinic/ClinicPublicPage";
import ClinicCustomPage from "./pages/clinic/ClinicCustomPage";
import ClinicTelehealthPolicy from "./pages/clinic/ClinicTelehealthPolicy";
const ClinicCategoryPage = lazy(() => import("./pages/clinic/ClinicCategoryPage"));
const ClinicBooking = lazy(() => import("./pages/clinic/ClinicBooking"));
const ClientPortal = lazy(() => import("./pages/clinic/ClientPortal"));
const ClinicClientPaymentCallback = lazy(() => import("./pages/clinic/ClinicClientPaymentCallback"));
const ClinicsManagement = lazy(() => import("./pages/ClinicsManagement"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const FeedbackRequestWrapper = () => {
  const { user, pendingRequest, showDialog, setShowDialog } = useFeedbackRequest();
  
  const currentPath = window.location.pathname;
  const isPublicClinicPage = currentPath.startsWith('/clinic/') && 
    !currentPath.includes('/dashboard') && 
    !currentPath.includes('/register') && 
    !currentPath.includes('/pending') && 
    !currentPath.includes('/payment');

  if (!user || !pendingRequest || isPublicClinicPage) return null;

  return (
    <FeedbackRequestDialog
      open={showDialog}
      onOpenChange={setShowDialog}
      user={user}
      requestId={pendingRequest.id}
    />
  );
};

// Static /clinic/* routes that are NOT the public clinic page (ClinicPublicPage
// is the only one that sets its own clinic-specific favicon via slug).
const CLINIC_STATIC_SEGMENTS = new Set([
  "start", "register", "pending", "payment", "payment-callback", "dashboard", "meal-designer-addon",
]);

const AppContentInner = () => {
  usePageVisitTracker();

  const location = useLocation();
  const slugMatch = location.pathname.match(/^\/clinic\/([^/]+)$/);
  const isClinicPublicPage = !!slugMatch && !CLINIC_STATIC_SEGMENTS.has(slugMatch[1]);
  // Skip the default favicon reset on the public clinic page — it sets its
  // own clinic-logo favicon, and since child effects run before this
  // ancestor's, this call would otherwise overwrite it back to the default.
  useDynamicFavicon(undefined, isClinicPublicPage);

  return null;
};

// Capture recovery hash early and redirect if needed
const RecoveryRedirect = () => {
  const hash = window.location.hash;
  const isRecovery = hash.includes("type=recovery") || hash.includes("access_token");
  if (isRecovery) {
    // Store in sessionStorage before Supabase clears the hash
    sessionStorage.setItem("dietitech_recovery", "true");
    if (window.location.pathname !== "/reset-password") {
      window.location.replace("/reset-password" + hash);
    }
  }
  return null;
};

const AppContent = () => {
  useActiveTheme();
  usePresenceTracker();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        Sentry.setUser({ id: session.user.id, email: session.user.email });
      } else {
        Sentry.setUser(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <BrowserRouter>
      <RecoveryRedirect />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<StartClinic />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/meal-designer" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/calculator" element={<CalorieCalculator />} />
          <Route path="/dietitian-calculator" element={<DietitianCalculator />} />
          <Route path="/renal-calculator" element={<RenalCalculator />} />
          <Route path="/formula-calculator" element={<FormulaCalculator />} />
          <Route path="/food-database" element={<FoodDatabase />} />
          <Route path="/meal-planner" element={<MealPlanner />} />
          <Route path="/user-management" element={<AdminProtectedRoute><UserManagement /></AdminProtectedRoute>} />
          <Route path="/analytics" element={<AdminProtectedRoute><Analytics /></AdminProtectedRoute>} />
          <Route path="/product-management" element={<AdminProtectedRoute><ProductManagement /></AdminProtectedRoute>} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/testimonials-management" element={<AdminProtectedRoute><TestimonialsManagement /></AdminProtectedRoute>} />
          <Route path="/contact-messages" element={<AdminProtectedRoute><ContactMessagesManagement /></AdminProtectedRoute>} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/purchases-management" element={<AdminProtectedRoute><PurchasesManagement /></AdminProtectedRoute>} />
          <Route path="/dashboard" element={<SpecialistDashboard />} />
          <Route path="/dashboard/reports" element={<PerformanceReports />} />
          <Route path="/renewal" element={<RenewalPayment />} />
          <Route path="/unlock-clients" element={<UnlockClients />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/free-trial" element={<FreeTrial />} />
          <Route path="/about" element={<About />} />
          <Route path="/special-services" element={<AdminProtectedRoute><SpecialServicesManagement /></AdminProtectedRoute>} />
          {/* Clinic Routes */}
          <Route path="/clinic/start" element={<Navigate to="/" replace />} />
          <Route path="/clinic/register" element={<ClinicRegister />} />
          <Route path="/clinic/pending" element={<ClinicPending />} />
          <Route path="/clinic/payment" element={<ClinicPayment />} />
          <Route path="/clinic/payment-callback" element={<ClinicPaymentCallback />} />
          <Route path="/clinic/dashboard" element={<ClinicDashboard />} />
          <Route path="/clinic/renew/:clinicId" element={<ClinicRenew />} />
          <Route path="/clinic/meal-designer-addon" element={<ClinicMealDesignerAddon />} />
          <Route path="/clinics-management" element={<AdminProtectedRoute><ClinicsManagement /></AdminProtectedRoute>} />
          <Route path="/org-dashboard" element={<OrgDashboard />} />
          <Route path="/clinic/:slug" element={<ClinicPublicPage />} />
          <Route path="/clinic/:slug/category/:categoryId" element={<ClinicCategoryPage />} />
          <Route path="/clinic/:slug/telehealth-policy" element={<ClinicTelehealthPolicy />} />
          <Route path="/clinic/:slug/:pageType" element={<ClinicCustomPage />} />
          <Route path="/clinic/:slug/book/:productId" element={<ClinicBooking />} />
          <Route path="/clinic/:slug/payment-callback" element={<ClinicClientPaymentCallback />} />
          <Route path="/clinic/:slug/portal" element={<ClientPortal />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <AppContentInner />
      <FeedbackRequestWrapper />
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <PlatformLogoProvider>
        <AdminImpersonationProvider>
          <MaintenanceMode>
            <Toaster />
            <Sonner />
            <Sentry.ErrorBoundary fallback={({ error }) => {
              if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
                return (
                  <div className="min-h-screen flex items-center justify-center">
                    <p className="text-muted-foreground">جارٍ تحديث الصفحة...</p>
                  </div>
                );
              }
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <p className="text-muted-foreground">حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة.</p>
                </div>
              );
            }}>
              <AppContent />
            </Sentry.ErrorBoundary>
          </MaintenanceMode>
        </AdminImpersonationProvider>
      </PlatformLogoProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
